import type { Persona } from '@repo/contracts';

/**
 * A persona's colour, as whole class names — `selection` is how their words
 * highlight when someone selects them, in their own colour.
 *
 * Written out rather than interpolated (`bg-persona-${accent}`): Tailwind scans
 * source for literal class strings and generates nothing for a name it never
 * sees, so a template literal produces a card with no background at all — and
 * it looks fine in a DOM snapshot test, because the className is still there.
 */
const ACCENTS = [
  {
    fill: 'bg-persona-0',
    ink: 'text-persona-0-ink',
    ring: 'ring-persona-0-ink/30',
    selection: 'selection:bg-persona-0-ink/25 selection:text-text-primary',
  },
  {
    fill: 'bg-persona-1',
    ink: 'text-persona-1-ink',
    ring: 'ring-persona-1-ink/30',
    selection: 'selection:bg-persona-1-ink/25 selection:text-text-primary',
  },
  {
    fill: 'bg-persona-2',
    ink: 'text-persona-2-ink',
    ring: 'ring-persona-2-ink/30',
    selection: 'selection:bg-persona-2-ink/25 selection:text-text-primary',
  },
  {
    fill: 'bg-persona-3',
    ink: 'text-persona-3-ink',
    ring: 'ring-persona-3-ink/30',
    selection: 'selection:bg-persona-3-ink/25 selection:text-text-primary',
  },
  {
    fill: 'bg-persona-4',
    ink: 'text-persona-4-ink',
    ring: 'ring-persona-4-ink/30',
    selection: 'selection:bg-persona-4-ink/25 selection:text-text-primary',
  },
  {
    fill: 'bg-persona-5',
    ink: 'text-persona-5-ink',
    ring: 'ring-persona-5-ink/30',
    selection: 'selection:bg-persona-5-ink/25 selection:text-text-primary',
  },
] as const;

export function accentOf(persona: Pick<Persona, 'accent'>) {
  return ACCENTS[persona.accent % ACCENTS.length]!;
}

/**
 * "Maya Iqbal" → "MI".
 *
 * Two letters at most, and the first letter alone for a single-word name —
 * an avatar is a shape to recognise, not a label to read.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? '?';
  const last = words.length > 1 ? words[words.length - 1]?.[0] : undefined;
  return `${first}${last ?? ''}`.toUpperCase();
}
